/**
 * Pictaphone Audio Engine
 * Web Audio API synthesis — no audio files needed.
 * Default: muted (opt-in). Preference stored in localStorage.
 */
import { SOUND_EFFECTS } from './featureFlags';

class AudioManager {
  constructor() {
    this._ctx = null;
    // Default muted — don't surprise users. 'false' stored explicitly means unmuted.
    this._muted = localStorage.getItem('pictaphone_audio_muted') !== 'false';
    this._drawingSource = null;
    this._drawingGain = null;
    this._drawingFilter = null;
  }

  get ctx() {
    if (!this._ctx) {
      this._ctx = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this._ctx;
  }

  get muted() { return !SOUND_EFFECTS || this._muted; }

  setMuted(value) {
    this._muted = value;
    localStorage.setItem('pictaphone_audio_muted', String(value));
    if (value) this._stopDrawingNoise();
  }

  toggle() {
    if (!SOUND_EFFECTS) return true;
    this.setMuted(!this._muted);
    return this._muted;
  }

  _resume() {
    const ctx = this.ctx;
    if (ctx.state === 'suspended') ctx.resume();
  }

  _makeNoise(duration = 1) {
    const ctx = this.ctx;
    const n = Math.floor(ctx.sampleRate * duration);
    const buf = ctx.createBuffer(1, n, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1;
    return buf;
  }

  // Core tone generator with ADSR-style envelope
  _tone(freq, duration, { type = 'sine', gain = 0.2, delay = 0, attack = 0.01 } = {}) {
    if (this._muted) return;
    this._resume();
    const ctx = this.ctx;
    const t = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.connect(g);
    g.connect(ctx.destination);
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(gain, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + duration);
    osc.start(t);
    osc.stop(t + duration + 0.05);
  }

  // ── Drawing scratch ──────────────────────────────────────────────────

  startDrawStroke(tool) {
    if (this._muted) return;
    this._resume();
    this._stopDrawingNoise();
    const ctx = this.ctx;
    const noise = this._makeNoise(2);
    const src = ctx.createBufferSource();
    src.buffer = noise;
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    if (tool === 'eraser') {
      filter.type = 'lowpass';
      filter.frequency.value = 500;
      filter.Q.value = 0.5;
    } else {
      filter.type = 'bandpass';
      filter.frequency.value = 3500;
      filter.Q.value = 0.7;
    }
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.03, ctx.currentTime);
    src.connect(filter);
    filter.connect(g);
    g.connect(ctx.destination);
    src.start();
    this._drawingSource = src;
    this._drawingGain = g;
    this._drawingFilter = filter;
  }

  updateDrawVelocity(velocityPxPerSec) {
    if (!this._drawingGain || this._muted) return;
    const ctx = this.ctx;
    const targetGain = Math.min(0.07, 0.008 + velocityPxPerSec * 0.0012);
    this._drawingGain.gain.setTargetAtTime(targetGain, ctx.currentTime, 0.04);
    if (this._drawingFilter) {
      const targetFreq = Math.min(5500, 1800 + velocityPxPerSec * 10);
      this._drawingFilter.frequency.setTargetAtTime(targetFreq, ctx.currentTime, 0.04);
    }
  }

  stopDrawStroke() {
    this._stopDrawingNoise();
  }

  _stopDrawingNoise() {
    if (!this._drawingSource) return;
    const ctx = this.ctx;
    const g = this._drawingGain;
    if (g) g.gain.setTargetAtTime(0, ctx.currentTime, 0.03);
    const src = this._drawingSource;
    setTimeout(() => { try { src.stop(); } catch {} }, 150);
    this._drawingSource = null;
    this._drawingGain = null;
    this._drawingFilter = null;
  }

  // ── Tool / color / fill ──────────────────────────────────────────────

  toolSwitch() {
    this._tone(1300, 0.09, { gain: 0.11, attack: 0.005 });
    this._tone(850, 0.07, { gain: 0.07, delay: 0.055 });
  }

  colorSwitch() {
    this._tone(1700, 0.06, { gain: 0.06, attack: 0.004 });
  }

  fillPop() {
    if (this._muted) return;
    this._resume();
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.connect(g);
    g.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(480, t);
    osc.frequency.exponentialRampToValueAtTime(160, t + 0.22);
    g.gain.setValueAtTime(0.16, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);
    osc.start(t);
    osc.stop(t + 0.3);
  }

  // ── Timer ────────────────────────────────────────────────────────────

  timerTick(secondsLeft) {
    if (this._muted) return;
    this._resume();
    if (secondsLeft <= 5) {
      this._tone(880, 0.07, { type: 'square', gain: 0.09, attack: 0.003 });
    } else {
      this._tone(520, 0.055, { gain: 0.065, attack: 0.003 });
    }
  }

  timerEnd() {
    if (this._muted) return;
    this._resume();
    [0, 0.13, 0.26, 0.39].forEach(delay => {
      this._tone(340, 0.1, { type: 'square', gain: 0.17, delay, attack: 0.005 });
    });
  }

  // ── Phase transition ─────────────────────────────────────────────────

  whoosh() {
    if (this._muted) return;
    this._resume();
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const buf = this._makeNoise(0.5);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(120, t);
    filter.frequency.exponentialRampToValueAtTime(2800, t + 0.32);
    filter.Q.value = 2.2;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.5, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.42);
    src.connect(filter);
    filter.connect(g);
    g.connect(ctx.destination);
    src.start(t);
    src.stop(t + 0.5);
  }

  // ── Submit ───────────────────────────────────────────────────────────

  submitChime() {
    if (this._muted) return;
    this._resume();
    // Ascending C-major arpeggio
    [
      { freq: 523, delay: 0 },
      { freq: 659, delay: 0.07 },
      { freq: 784, delay: 0.14 },
      { freq: 1047, delay: 0.21 },
    ].forEach(({ freq, delay }) => {
      this._tone(freq, 0.32, { type: 'triangle', gain: 0.16, delay, attack: 0.01 });
    });
  }

  // ── Results ──────────────────────────────────────────────────────────

  resultsFanfare() {
    if (this._muted) return;
    this._resume();
    const melody = [
      { f: 523, d: 0.09, t: 0 },
      { f: 659, d: 0.09, t: 0.1 },
      { f: 784, d: 0.09, t: 0.2 },
      { f: 1047, d: 0.38, t: 0.3 },
      { f: 784, d: 0.09, t: 0.32 },
      { f: 1047, d: 0.55, t: 0.46 },
    ];
    melody.forEach(({ f, d, t }) => {
      this._tone(f, d, { type: 'triangle', gain: 0.17, delay: t, attack: 0.01 });
    });
  }

  bubblePop() {
    if (this._muted) return;
    this._resume();
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.connect(g);
    g.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(620, t);
    osc.frequency.exponentialRampToValueAtTime(290, t + 0.065);
    g.gain.setValueAtTime(0.22, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.08);
    osc.start(t);
    osc.stop(t + 0.1);
  }

  typingTick() {
    if (this._muted) return;
    this._resume();
    this._tone(1900, 0.022, { gain: 0.035, attack: 0.003 });
  }

  chainComplete() {
    if (this._muted) return;
    this._resume();
    [784, 659, 523].forEach((freq, i) => {
      this._tone(freq, 0.26, { type: 'triangle', gain: 0.14, delay: i * 0.09, attack: 0.01 });
    });
  }

  // ── Lobby ────────────────────────────────────────────────────────────

  playerJoin() {
    this._tone(880, 0.14, { gain: 0.13, attack: 0.008 });
    this._tone(1100, 0.14, { gain: 0.09, delay: 0.1, attack: 0.008 });
  }

  countdownBeep(n) {
    if (this._muted) return;
    this._resume();
    if (n === 0) {
      [523, 784, 1047].forEach((f, i) =>
        this._tone(f, 0.28, { type: 'triangle', gain: 0.19, delay: i * 0.07, attack: 0.01 })
      );
    } else {
      this._tone(440, 0.17, { gain: 0.16, attack: 0.008 });
    }
  }
}

export const audio = new AudioManager();
